<script lang="ts" context="module">
  export enum ACMountType {
    GROUND_MOUNT = 'GROUND_MOUNT',
    WALL_MOUNT = 'WALL_MOUNT',
  }
</script>

<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  import { getAppConfig } from '../../../common/config/app-config';

  import { getPriceHTMLOrFree, getStr, slideOpenAnimation } from '../../../common/util';
  import Icon from '../../common/components/Icon.svelte';
  import { pdpServicesSaveState, pdpSplitAcServiceSelectedItem, setMixpanelDataForService } from '../store/pdp_store';
  import PdpAdditionalServicesMiniCard from './PDPAdditionalServicesMiniCard.svelte';
  import PdpServiceMiniCard from './PDPServiceMiniCard.svelte';
  import PdpVasPriceTile from './PDPVasPriceTile.svelte';

  export let data: any;
  export let labelData: any;
  export let lmdConfigurations: any;

  const {
    CSRFToken,
    productDetails: { code: pdpProductCode },
  } = getAppConfig();

  let activeSetupOption = null;

  $: setupOptions = generateACSetupOptions(data);
  $: selectedSetupInfo = getSelectedSetupInfo(activeSetupOption);
  $: hasSelectedSetupInfo = selectedSetupInfo?.info?.length > 0 || selectedSetupInfo?.bottomInfoLabelText?.length > 0;
  $: updatePDPStoreWithSelectedItem(activeSetupOption);

  onMount(() => {
    const savedState = $pdpServicesSaveState.acSetup;
    if (savedState) {
      activeSetupOption = setupOptions.find(o => o.mount === savedState.activeSetupOption.mount);
    }
  });
  onDestroy(() => {
    pdpServicesSaveState.setAcSetup(activeSetupOption);
  });

  function updatePDPStoreWithSelectedItem(activeSetupOption) {
    console.log('PDPSplitACService ::: activeSetupOption ::: ', activeSetupOption);
    if (activeSetupOption !== null) {
      const acSetupAddedAsFree = activeSetupOption?.servicePrice?.value === 0;
      let vasProductCode = data?.serviceProductCode;
      if (activeSetupOption?.mount === ACMountType.WALL_MOUNT) {
        if (acSetupAddedAsFree) {
          vasProductCode = data?.bracketProduct?.code;
        } else {
          // add serviceProductCode as well as brackProductCode
          if (activeSetupOption?.installationPrice?.value !== 0) {
            vasProductCode = [data?.serviceProductCode, data?.bracketProduct?.code].join(',');
          } else {
            vasProductCode = data?.bracketProduct?.code;
          }
        }
      }
      // activeSetupOption?.mount === ACMountType.WALL_MOUNT ? data?.bracketProduct?.code : data?.serviceProductCode;
      const { priceType, priceTo, defaultWarranty } = data.vasPrices[0];

      setMixpanelDataForService(
        'Split AC',
        activeSetupOption?.vasType?.labelEn || 'no plan',
        'no addon',
        activeSetupOption?.servicePrice?.value || 0,
        vasProductCode
      );

      $pdpSplitAcServiceSelectedItem = {
        vasProductCode,
        vasType: activeSetupOption?.vasType?.code,
        priceType,
        priceTo,
        productCode: pdpProductCode,
        qty: 1,
        onWallService: activeSetupOption?.mount === ACMountType.WALL_MOUNT,
        splitAcService: data?.splitAcService,
        acSetupAddedAsFree,
        CSRFToken,
        defaultWarranty,
      };
    } else {
      setMixpanelDataForService('Split AC');
      $pdpSplitAcServiceSelectedItem = null;
    }
  }
  function generateACSetupOptions(data) {
    const options = [];

    if (data?.vasPrices?.length > 0) {
      // ground mount
      options.push({
        mount: ACMountType.GROUND_MOUNT,
        vasType: {
          ...data?.vasPrices[0]?.vasType,
          name: null,
          label: getStr('extra.services.pdp.ac.floor.mount.label'),
        },
        servicePrice: data?.vasPrices[0]?.servicePrice,
      });

      // wall mount
      if (data?.vasPrices[0]?.vasType?.bracketAllowed) {
        const bracketPriceValue = data?.bracketProduct?.price.value || 0;
        const installationPriceValue = data?.vasPrices[0]?.servicePrice.value || 0;
        const servicePrice = Object.assign({}, data?.bracketProduct?.price, {
          value: bracketPriceValue + installationPriceValue,
        });

        options.push({
          mount: ACMountType.WALL_MOUNT,
          vasType: {
            ...data?.vasPrices[0]?.vasType,
            name: null,
            label: getStr('extra.services.pdp.ac.wall.mount.label'),
          },
          servicePrice,
          bracketPrice: data?.bracketProduct?.price,
          installationPrice: data?.vasPrices[0]?.servicePrice,
        });
      }
    }

    return options;
  }
  function handleVasPriceTileClick(vasPrice) {
    activeSetupOption = activeSetupOption === vasPrice ? null : vasPrice;
  }
  function getSelectedSetupInfo(selectedSetupOption) {
    const info = [];
    let bottomInfoLabelText = '';

    if (selectedSetupOption?.mount === ACMountType.GROUND_MOUNT) {
      // for ground mount, only installation fee is to be shown
      info.push([getStr('extra.services.pdp.ac.installment.fee.text'), selectedSetupOption?.servicePrice?.value]);
      bottomInfoLabelText = getStr('extra.ac.floor.notes');
    } else if (selectedSetupOption?.mount === ACMountType.WALL_MOUNT) {
      // for wall mount, installation fee and bracket fee is to be shown
      info.push([getStr('extra.services.pdp.ac.installment.fee.text'), selectedSetupOption?.installationPrice?.value]);
      info.push([getStr('extra.services.pdp.ac.bracket.fee.text'), selectedSetupOption?.bracketPrice?.value]);
      bottomInfoLabelText = getStr('extra.ac.wall.notes');
    } else {
      return null;
    }

    return { info, bottomInfoLabelText };
  }
</script>

<PdpServiceMiniCard data={labelData} serviceBlockType={data.serviceBlockType}>
  <section class="ac-services">
    {#each setupOptions as vasPrice}
      <PdpVasPriceTile
        data={vasPrice}
        variant="ac-mount"
        active={activeSetupOption === vasPrice}
        on:click={() => handleVasPriceTileClick(vasPrice)}
      />
    {/each}
  </section>
  {#if hasSelectedSetupInfo}
    <section class="selected-setup-info" transition:slideOpenAnimation>
      {#if selectedSetupInfo?.info?.length > 0}
        <section class="info-list">
          {#each selectedSetupInfo.info as infoRow}
            <section class="info-row" transition:slideOpenAnimation>
              <section class="data">{infoRow[0]}</section>
              <section class="data">
                {@html getPriceHTMLOrFree(infoRow[1])}
              </section>
            </section>
          {/each}
        </section>
      {/if}
      {#if selectedSetupInfo?.bottomInfoLabelText?.length > 0}
        <section class="bottom-label">
          <Icon type="font" glyph="information" size={20} />
          <section class="text">
            {selectedSetupInfo.bottomInfoLabelText}
          </section>
        </section>
      {/if}
    </section>
  {/if}

  <PdpAdditionalServicesMiniCard data={lmdConfigurations} />
</PdpServiceMiniCard>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  .ac-services {
    display: flex;
    gap: 12px;
  }
  .selected-setup-info {
    margin-top: 28px;

    .info-list {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .info-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 16px;
        line-height: 18px;
        font-weight: 500;
        color: $black;
      }
    }
    .bottom-label {
      color: $icon-only-icon-blue;
      background-color: $neutral-light-03;
      border-radius: 8px;
      display: flex;
      padding: 8px;
      gap: 5px;
      align-items: flex-start;
      margin-top: 20px;

      .text {
        color: $neutral-dark-02;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        padding-top: 2px;
      }
    }
  }
</style>
